import { useContext, useState, useEffect } from "react";
import {
  Box,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  Divider,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { CacheContext } from "../../../Context/cacheContext";
import { VehicleContext } from "../../../Context/vehicleContext";
import { Vocabulary } from "../../../Utils/Vocabulary";
import {
  getActiveLocations,
  userInputIcon,
  getBnrEuroRate,
} from "../../../Utils/utils";
import { PurchaseTypes, VehiclesStatus } from "../../../Utils/autocompleteEnum";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { administrativeSchema } from "../../../Validators/vehicleValidators";
import { StepActions } from "../StepActions";
import { useAIProcessing } from "../../../Utils/AIProcessingOverlay";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import { useAutocompleteModal } from "./useAutocompleteModal";

interface VehicleAdministrativeFormProps {
  onSubmit?: (data: any) => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

/**
 * A reusable component for displaying a form section.
 * It displays a title and a container for the form fields.
 * The form fields are passed as children to the component.
 *
 * @param {string} title - The title of the form section
 * @param {React.ReactNode} children - The form fields to be displayed in the form section
 * @returns {React.ReactNode} - A JSX element representing the form section
 */
export const FormSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3 }}>
        {title}
      </Typography>

      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  );
};

/**
 * A type for generic object
 */
type GenericType =
  | { nume?: string; nume_firma?: string; name?: string; [key: string]: any }
  | null
  | undefined;

/**
 * Administrative form component for vehicle creation.
 * Handles user assignments, purchase information, pricing, and vehicle characteristics.
 */
const VehicleAdministrativeForm: React.FC<VehicleAdministrativeFormProps> = ({
  onSubmit,
  onPrevious,
  isFirstStep = false,
  isLastStep = false,
}) => {
  const cacheContext = useContext(CacheContext);
  const vehicleContext = useContext(VehicleContext) as any;
  const navigate = useNavigate();
  const { startProcessing, stopProcessing } = useAIProcessing();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Access vehicle from context
  const vehicle = vehicleContext.state?.vehicle || vehicleContext.vehicle;

  // Hook for adding/editing dropdown options
  const {
    DropDownButton,
    AutocompleteModalComponent,
    UserAddButton,
    CompanyAddButton,
    UserModalComponent,
    CompanyModalComponent,
  } = useAutocompleteModal();

  const { control, handleSubmit, setValue, watch, getValues } = useForm({
    resolver: yupResolver(administrativeSchema),
    defaultValues: {
      user_achizitie_r: vehicle?.user_achizitie_r || undefined,
      user_logistica_r: vehicle?.user_logistica_r || undefined,
      user_intermediere: vehicle?.user_intermediere || undefined,
      vanzator: vehicle?.vanzator || undefined,
      tip_achizitie_r: vehicle?.tip_achizitie_r || undefined,
      firma_achizitie_r: vehicle?.firma_achizitie_r || undefined,
      furnizor_r: vehicle?.furnizor_r || undefined,
      locatie_administrativa_r: vehicle?.locatie_administrativa_r || undefined,
      physical_location: vehicle?.physical_location || "",
      tip_tva_r: vehicle?.tip_tva_r || undefined,
      cursEuro: vehicle?.cursEuro || "",
      pret_achizitie_euro: vehicle?.pret_achizitie_euro || "",
      pret_achizitie_ron: vehicle?.pret_achizitie_ron || "",
      pret_vanzare_euro: vehicle?.pret_vanzare_euro || "",
      pret_vanzare_ron: vehicle?.pret_vanzare_ron || "",
      pret_minim_vanzare: vehicle?.pret_minim_vanzare || "",
      bonus_vanzare_vehicul: vehicle?.bonus_vanzare_vehicul || "",
      data_achizitie: vehicle?.data_achizitie || null,
      data_receptie: vehicle?.data_receptie || null,
      retromobil: vehicle?.retromobil || "0",
      carte_service: vehicle?.carte_service || "0",
      tuning: vehicle?.tuning || "0",
      volan: vehicle?.volan || "0",
    },
  });

  // Fetch BNR exchange rate on mount
  useEffect(() => {
    console.log("Vehicle data on mount:", vehicle?.cursEuro);
    if (!vehicle?.cursEuro || vehicle?.cursEuro == "1") {
      getBnrEuroRate().then((rate) => {
        if (rate) {
          setValue("cursEuro", parseFloat(rate.toString()).toFixed(4));
        }
      });
    }
  }, []);

  // Save form data to context on unmount (for back navigation)
  useEffect(() => {
    return () => {
      const currentValues = getValues();
      vehicleContext.updateVehicle({
        ...vehicle,
        ...currentValues,
        tip_achizitie_id: currentValues.tip_achizitie_r?.id,
        firma_achizitie_id: currentValues.firma_achizitie_r?.id,
        furnizor_id: currentValues.furnizor_r?.id,
        locatie_administrativa_id: currentValues.locatie_administrativa_r?.id,
        tip_tva_id: currentValues.tip_tva_r?.id,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch tip_achizitie to show/hide conditional fields
  const tipAchizitie = watch("tip_achizitie_r");

  // Watch price fields for auto-calculation
  const pretVanzareEuro = watch("pret_vanzare_euro");
  const cursEuro = watch("cursEuro");

  // Auto-calculate RON price when EUR price changes
  useEffect(() => {
    if (pretVanzareEuro && cursEuro) {
      const euroValue =
        parseFloat(String(pretVanzareEuro).replace(/\s/g, "")) || 0;
      const rate = parseFloat(String(cursEuro)) || 1;
      const ronValue = euroValue * rate;
      setValue("pret_vanzare_ron", ronValue);
    }
  }, [pretVanzareEuro, cursEuro, setValue]);

  /**
   * Determine the initial status based on purchase type
   */
  const getInitialStatus = (tipAchizitieId: number | undefined) => {
    const tipAchizitie = cacheContext.cache?.purchaseType?.find(
      (item: any) => item.id === tipAchizitieId,
    );
    const inTransitStatus = cacheContext.cache?.vehicleStatus?.find(
      (status: any) => status.nume === VehiclesStatus.inTransitStatus,
    );

    if (
      tipAchizitie?.nume === PurchaseTypes.buyBack ||
      tipAchizitie?.nume === PurchaseTypes.national
    ) {
      const receivedStatus = cacheContext.cache?.vehicleStatus?.find(
        (status: any) => status.nume === VehiclesStatus.received,
      );
      return {
        status_id: receivedStatus?.id,
        physical_status_id: receivedStatus?.id,
        main_status_id: receivedStatus?.id,
      };
    }

    const unpaidStatus = cacheContext.cache?.vehicleStatus?.find(
      (status: any) => status.nume === VehiclesStatus.unpaid,
    );
    return {
      status_id: unpaidStatus?.id,
      physical_status_id: inTransitStatus?.id,
      main_status_id: inTransitStatus?.id,
    };
  };

  const onSubmitForm = async (data: any) => {
    const transformedData = {
      ...data,
      user_achizitie_id: data.user_achizitie_r?.id,
      user_logistica_id: data.user_logistica_r?.id,
      user_intermediere_id: data.user_intermediere?.id,
      vanzator_id: data.vanzator?.id,
      tip_achizitie_id: data.tip_achizitie_r?.id,
      firma_achizitie_id: data.firma_achizitie_r?.id,
      furnizor_id: data.furnizor_r?.id,
      locatie_administrativa_id: data.locatie_administrativa_r?.id,
      tva_id: data.tip_tva_r?.id,
      // Format dates
      data_achizitie: data.data_achizitie
        ? moment(data.data_achizitie).format("YYYY-MM-DD HH:mm:ss")
        : null,
      data_receptie: data.data_receptie
        ? moment(data.data_receptie).format("YYYY-MM-DD HH:mm:ss")
        : null,
    };

    // Save to vehicle context
    vehicleContext.updateVehicle({
      ...vehicle,
      ...transformedData,
    });

    // If it's the last step, create the vehicle
    if (isLastStep) {
      setIsSubmitting(true);
      startProcessing(Vocabulary.creatingVehicle);

      try {
        // Get initial status based on purchase type
        const statusData = getInitialStatus(transformedData.tip_achizitie_id);

        // Filter out null/undefined from dotari - keep as objects, context will extract IDs
        const dotariFiltered = (vehicle?.dotari || []).filter(
          (d: any) =>
            d !== null &&
            d !== undefined &&
            d.id !== null &&
            d.id !== undefined,
        );

        // Prepare final vehicle data
        const finalVehicleData = {
          ...vehicle,
          ...transformedData,
          ...statusData,
          // Handle default values
          pret_minim_vanzare: transformedData.pret_minim_vanzare || 0,
          numar_portiere: vehicle?.numar_portiere || 5,
          nr_locuri: vehicle?.nr_locuri || 5,
          kilometri: vehicle?.kilometri || 0,
          an_fabricatie: vehicle?.an_fabricatie || 0,
          // Handle dotari (options) - keep as objects, context extracts IDs
          dotari: dotariFiltered,
          // Vehicle characteristics
          retromobil: transformedData.retromobil || "0",
          carte_service: transformedData.carte_service || "0",
          tuning: transformedData.tuning || "0",
          volan: transformedData.volan || "0",
        };

        // Create the vehicle with timeout to prevent hanging
        const result = await vehicleContext.saveWithoutFile(
          () => true,
          null,
          null,
          finalVehicleData,
          true
        ).finally(() => {
          stopProcessing();
          setIsSubmitting(false);
        });
       

        // Check if we got a valid result
        if (!result) {
          throw new Error("No response from server");
        }

        // Get the new vehicle ID
        const newVehicleId = result?.data?.id || result?.id;

        // Save main image if exists
        if (newVehicleId && vehicle?.main_image_file) {
          try {
            await vehicleContext.saveMainImage(
              vehicle.main_image_file,
              newVehicleId,
            );
          } catch (imageError) {
            console.error("Error saving main image:", imageError);
            // Continue anyway - vehicle was created
          }
        }

        // Navigate to the new vehicle
        if (newVehicleId) {
          // Don't reset context - saveWithoutFile already updated it with the saved vehicle data
          stopProcessing();
          setIsSubmitting(false);
          window.scrollTo(0, 0);
          navigate(`/vehiclesDashboard/vehicleDetails/${newVehicleId}`);
        } else {
          console.error("Vehicle created but no ID returned:", result);
          throw new Error("Vehicle created but no ID returned");
        }
      } catch (error: any) {
        // Stop overlay FIRST so user can see the error
        stopProcessing();
        setIsSubmitting(false);
      }
    } else if (onSubmit) {
      onSubmit(transformedData);
    }
  };

  const handleNextClick = async () => {
    if (isSubmitting) return false;

    // Use handleSubmit which validates and calls the appropriate handler
    const success = await new Promise<boolean>((resolve) => {
      handleSubmit(
        async (data) => {
          await onSubmitForm(data);
          resolve(true);
        },
        (validationErrors) => {
          // Show warning toast for validation errors
          if (Object.keys(validationErrors).length > 0) {
            toast.warning(Vocabulary.fillInRequiredFields);

            // Scroll to first error field
            const firstErrorKey = Object.keys(validationErrors)[0];
            if (firstErrorKey) {
              const errorElement =
                document.querySelector(`[name="${firstErrorKey}"]`) ||
                document.getElementById(firstErrorKey);
              if (errorElement) {
                errorElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                (errorElement as HTMLElement).focus?.();
              }
            }
          }
          resolve(false);
        },
      )();
    });
    return success;
  };
  /**
   * Render the user section of the form.
   * This section contains the user achizitie, user logistica and vanzator fields.
   * @returns {JSX.Element} The rendered user section.
   */
  const renderUserSection = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          {/* User Achizitie */}
          <Controller
            name="user_achizitie_r"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                size="small"
                disableClearable
                disablePortal
                PaperComponent={UserAddButton}
                getOptionLabel={(option) =>
                  (option as GenericType)?.nume ||
                  (option as GenericType)?.name ||
                  ""
                }
                onChange={(_event, value) => {
                  field.onChange(value);
                  setValue("user_achizitie_r", value);
                }}
                options={cacheContext.cache?.users || []}
                renderInput={(params) => (
                  <TextField
                    required
                    error={!!error}
                    helperText={error?.message}
                    label="Utilizator Achizitie"
                    {...params}
                    {...userInputIcon(params)}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          {/* User Logistica */}
          <Controller
            name="user_logistica_r"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                size="small"
                disableClearable
                disablePortal
                PaperComponent={UserAddButton}
                getOptionLabel={(option) =>
                  (option as GenericType)?.nume ||
                  (option as GenericType)?.name ||
                  ""
                }
                onChange={(_event, value) => {
                  field.onChange(value);
                }}
                options={cacheContext.cache?.users || []}
                renderInput={(params) => (
                  <TextField
                    required
                    error={!!error}
                    helperText={error?.message}
                    label="Utilizator Logistica"
                    {...params}
                    {...userInputIcon(params)}
                  />
                )}
              />
            )}
          />
        </Grid>

        {/* Conditional User Intermediere - Show only when tip_achizitie is "Intermediere" */}
        {tipAchizitie?.nume === PurchaseTypes.intemediated && (
          <Grid item xs={12} md={4}>
            <Controller
              name="user_intermediere"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  size="small"
                  disableClearable
                  disablePortal
                  PaperComponent={UserAddButton}
                  getOptionLabel={(option) =>
                    (option as GenericType)?.nume ||
                    (option as GenericType)?.name ||
                    ""
                  }
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  options={cacheContext.cache?.users || []}
                  renderInput={(params) => (
                    <TextField
                      required
                      error={!!error}
                      helperText={error?.message}
                      label={Vocabulary.userIntermediere}
                      {...params}
                      {...userInputIcon(params)}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  const renderCompanyAndPurchaseInfoSection = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {/* Tip Achizitie */}
          <Controller
            name="tip_achizitie_r"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                size="small"
                disableClearable
                disablePortal
                PaperComponent={DropDownButton}
                getOptionLabel={(option) => (option as GenericType)?.nume || ""}
                onChange={(_event, value) => {
                  field.onChange(value);
                }}
                options={cacheContext.cache?.purchaseType || []}
                renderInput={(params) => (
                  <TextField
                    required
                    error={!!error}
                    helperText={error?.message}
                    label="Tip achizitie"
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {/* Firma Achizitie */}
          <Controller
            name="firma_achizitie_r"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                id="firma_achizitie_r"
                size="small"
                disableClearable
                disablePortal
                PaperComponent={CompanyAddButton}
                getOptionLabel={(option) =>
                  (option as GenericType)?.nume_firma || ""
                }
                onChange={(_event, value) => {
                  field.onChange(value);
                }}
                options={cacheContext.cache?.company || []}
                renderInput={(params) => (
                  <TextField
                    required
                    error={!!error}
                    helperText={error?.message}
                    label="Firma Achizitie"
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {/* Furnizor */}
          <Controller
            name="furnizor_r"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                size="small"
                disableClearable
                disablePortal
                PaperComponent={DropDownButton}
                getOptionLabel={(option) => (option as GenericType)?.nume || ""}
                onChange={(_event, value) => {
                  field.onChange(value);
                }}
                options={cacheContext.cache?.providers || []}
                renderInput={(params) => (
                  <TextField
                    required
                    error={!!error}
                    helperText={error?.message}
                    label={Vocabulary.supplier}
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {/* Tip TVA */}
          <Controller
            name="tip_tva_r"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                id="tip_tva_r"
                size="small"
                disableClearable
                disablePortal
                PaperComponent={DropDownButton}
                getOptionLabel={(option) =>
                  `${(option as GenericType)?.nume} ${
                    (option as GenericType)?.valoare !== "0"
                      ? `(${(option as GenericType)?.valoare}%)`
                      : ""
                  }`
                }
                onChange={(_event, value) => {
                  field.onChange(value);
                }}
                options={cacheContext.cache?.tvaType || []}
                renderInput={(params) => (
                  <TextField
                    required
                    error={!!error}
                    helperText={error?.message}
                    label={Vocabulary.tipTVA}
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>
        {/* Data Receptie - Conditional based on tip_achizitie */}
        {(tipAchizitie?.nume === PurchaseTypes.buyBack ||
          tipAchizitie?.nume === PurchaseTypes.national ||
          tipAchizitie?.nume === PurchaseTypes.intemediated) && (
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Controller
                name="data_receptie"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    value={value ? moment(value) : null}
                    onChange={onChange}
                    label={Vocabulary.receptionDate}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderLocationSection = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {/* Locatie Administrativa */}
          <Controller
            name="locatie_administrativa_r"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                id="locatie_administrativa_r"
                size="small"
                disableClearable
                disablePortal
                PaperComponent={DropDownButton}
                getOptionLabel={(option) => (option as GenericType)?.nume || ""}
                onChange={(_event, value) => {
                  field.onChange(value);
                }}
                options={getActiveLocations(cacheContext.cache || [])}
                renderInput={(params) => (
                  <TextField
                    required
                    error={!!error}
                    helperText={error?.message}
                    label={Vocabulary.administrativeLocation}
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {/* Physical Location */}
          <Controller
            name="physical_location"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                freeSolo
                {...field}
                size="small"
                disablePortal
                getOptionLabel={(option) => {
                  if (typeof option === "string") {
                    return option;
                  }
                  return (option as GenericType)?.nume || "";
                }}
                onChange={(_event, value) => {
                  if (typeof value === "string") {
                    field.onChange(value);
                  } else if (value && (value as GenericType)?.nume) {
                    field.onChange((value as GenericType)?.nume);
                  } else {
                    field.onChange("");
                  }
                }}
                onInputChange={(_event, newInputValue) => {
                  field.onChange(newInputValue);
                }}
                options={getActiveLocations(cacheContext.cache || [])}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                renderInput={(params) => (
                  <TextField
                    error={!!error}
                    helperText={error?.message}
                    placeholder="Caută sau adaugă o locație nouă..."
                    label={Vocabulary.physicalLocation}
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>
    );
  };

  const renderPriceSection = () => {
    return (
      <Grid container spacing={2}>
        {/* Exchange Rate */}
        <Grid item xs={12}>
          <Controller
            name="cursEuro"
            control={control}
            render={({ field: { value } }) => (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: "rgba(255, 102, 51, 0.08)",
                  border: "1px solid rgba(255, 102, 51, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    display: "block",
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Curs valutar BNR (€ → Lei)
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "#FF6633",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <span style={{ fontSize: "0.85em", opacity: 0.7 }}>1 €</span>
                  <span style={{ fontSize: "0.7em", opacity: 0.6 }}>=</span>
                  <span>{Math.round(value * 100) / 100 || "—"} Lei</span>
                </Typography>
              </Box>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Selling Prices */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
            Prețuri de vânzare
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="pret_vanzare_euro"
            control={control}
            render={({ field: { value, onChange } }) => (
              <NumericFormat
                customInput={TextField}
                fullWidth
                size="small"
                thousandSeparator=" "
                decimalScale={0}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">€</InputAdornment>
                  ),
                }}
                onValueChange={(values) => onChange(values.floatValue || "")}
                value={value}
                label={Vocabulary.sellingPriceEuro}
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="pret_vanzare_ron"
            control={control}
            render={({ field: { value } }) => (
              <NumericFormat
                customInput={TextField}
                fullWidth
                size="small"
                thousandSeparator=" "
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">Lei</InputAdornment>
                  ),
                  readOnly: true,
                }}
                value={value}
                label={Vocabulary.sellingPriceLei}
                variant="outlined"
                sx={{ "& .MuiInputBase-input": { bgcolor: "#f5f5f5" } }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Minimum Sale Price */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
            Preț minim
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="pret_minim_vanzare"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TextField
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">€</InputAdornment>
                  ),
                }}
                onChange={onChange}
                value={value}
                type="number"
                label={Vocabulary.minimumSalePrice}
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Bonus Vanzare */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
            Bonus vânzare
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="bonus_vanzare_vehicul"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TextField
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">Lei</InputAdornment>
                  ),
                }}
                onChange={onChange}
                value={value}
                type="number"
                label={Vocabulary.vehicleSellingBonus}
                variant="outlined"
              />
            )}
          />
        </Grid>
      </Grid>
    );
  };

  const renderVehicleCharacteristicsSection = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name="retromobil"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value === "1"}
                    onChange={(e) => onChange(e.target.checked ? "1" : "0")}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#FF6633",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#FF6633",
                        },
                    }}
                  />
                }
                label="Retromobil"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name="carte_service"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value === "1"}
                    onChange={(e) => onChange(e.target.checked ? "1" : "0")}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#FF6633",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#FF6633",
                        },
                    }}
                  />
                }
                label={Vocabulary.serviceBook}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name="tuning"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value === "1"}
                    onChange={(e) => onChange(e.target.checked ? "1" : "0")}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#FF6633",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#FF6633",
                        },
                    }}
                  />
                }
                label={Vocabulary.tuning}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Controller
            name="volan"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value === "1"}
                    onChange={(e) => onChange(e.target.checked ? "1" : "0")}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#FF6633",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#FF6633",
                        },
                    }}
                  />
                }
                label="Volan pe dreapta"
              />
            )}
          />
        </Grid>
      </Grid>
    );
  };

  /**
   * Render the form
   */
  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmitForm)}
        sx={{ width: "100%" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <FormSection title={Vocabulary.userAndRoles}>
            {renderUserSection()}
          </FormSection>

          <FormSection title={Vocabulary.companyAndPurchaseInfoSection}>
            {renderCompanyAndPurchaseInfoSection()}
          </FormSection>

          <FormSection title={Vocabulary.locationSection}>
            {renderLocationSection()}
          </FormSection>

          <FormSection title={Vocabulary.pricingInformationSection}>
            {renderPriceSection()}
          </FormSection>

          <FormSection title="Caracteristici vehicul">
            {renderVehicleCharacteristicsSection()}
          </FormSection>
        </Box>
      </Box>

      <StepActions
        onNext={handleNextClick}
        onPrevious={onPrevious}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
      />

      {/* Modal for adding/editing dropdown options */}
      <AutocompleteModalComponent />
      <UserModalComponent />
      <CompanyModalComponent />
    </Box>
  );
};

export default VehicleAdministrativeForm;
